import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Download, FileQuestion, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { useAuthStore } from '@features/authentication/store/authStore';
import { tokenService } from '@features/authentication/services/tokenService';
import logoOnly from '@/assets/images/logo/csc_logo_logo_only.svg';
import { PdfDocumentViewer } from '../components/viewer/PdfDocumentViewer';
import { documentCaptureService } from '../services/documentCaptureService';
import { publicDocumentService } from '../services/publicDocumentService';
import type { PublicDocument } from '../types/documentCapture.types';

/**
 * `/p/:token` — the one link (86d3uu104, T10). It is what the NetSuite field points at for every
 * document family, and what the opener sees is decided HERE, by who they are:
 *
 *   • signed in with the family's view permission → the full internal viewer (`/d/{docNumber}`);
 *   • anyone else → the minimal customer view, but only for the families cleared for it;
 *   • everything else → one neutral "unavailable".
 *
 * The server is the authority throughout: resolve answers a uniform 404 for "unknown token" AND "not
 * yours" alike, so this page cannot be used to probe whether a document exists.
 *
 * Standalone by design: NOT inside AppLayout, and — critically — the authenticated resolve goes
 * through a bare fetch, never `apiClient`, whose 401 handler hard-navigates to /login. A customer
 * carrying a stale session must never be dumped on a staff login page.
 */
type Phase =
  | { state: 'resolving' }
  | { state: 'redirect'; docNumber: string; supersededLink: boolean }
  | { state: 'minimal'; doc: PublicDocument }
  | { state: 'unavailable' }
  | { state: 'error' };

export function PublicDocumentPage() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>({ state: 'resolving' });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  // Subscribe to the VALUE (CLAUDE.md 86d3d1z40): AuthInitializer has already validated or cleared
  // the session before any route renders, so this is trustworthy by the time we read it.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userName = useAuthStore((s) => s.user?.fullName ?? s.user?.username ?? null);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setPhase({ state: 'unavailable' });
      return;
    }
    setPhase({ state: 'resolving' });

    void (async () => {
      // 1. Signed in? Ask whether this token is one we may open in full.
      if (isAuthenticated) {
        try {
          const resolved = await documentCaptureService.resolveToken(token, tokenService.getAccessToken());
          if (cancelled) return;
          if (resolved) {
            // Carry `superseded` through — a staffer who followed a REPLACED link must be told, or the
            // document renders perfectly and they tell the customer "works on my end". Pasting the
            // customer's broken link is the whole reason staff can resolve a dead token at all.
            setPhase({ state: 'redirect', docNumber: resolved.docNumber, supersededLink: resolved.superseded });
            return;
          }
          // null = 401/403/404 — not ours to open in full. Fall through to the minimal view.
        } catch {
          // 5xx or the network. Do NOT silently downgrade a permitted staffer to the customer view —
          // that would quietly hide metadata they are entitled to. Offer a retry instead.
          if (cancelled) return;
          setPhase({ state: 'error' });
          return;
        }
      }

      // 2. Minimal (customer) branch — anonymous, or signed in without this family's permission.
      const doc = await publicDocumentService.get(token);
      if (cancelled) return;
      setPhase(doc ? { state: 'minimal', doc } : { state: 'unavailable' });
    })();

    return () => {
      cancelled = true;
    };
  }, [token, isAuthenticated, attempt]);

  // Fetch the PDF bytes only once the minimal branch is chosen. The endpoint answers
  // Content-Disposition: attachment, so pointing a frame straight at it would download, not display.
  // Plain fetch — never apiClient — so a customer is never bounced to /login.
  useEffect(() => {
    if (phase.state !== 'minimal' || !token) return;
    let cancelled = false;
    let url: string | null = null;
    fetch(publicDocumentService.fileUrl(token))
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (cancelled || !blob) return;
        url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setPdfUrl(url);
      })
      .catch(() => {
        /* the Download button below remains a working path */
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [phase.state, token]);

  const download = useCallback(() => {
    if (!token) return;
    const a = document.createElement('a');
    a.href = publicDocumentService.fileUrl(token);
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [token]);

  // Keep these pages out of search results — a link is meant to be forwardable, not published.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  if (phase.state === 'redirect') {
    return (
      <Navigate
        to={`/d/${encodeURIComponent(phase.docNumber)}`}
        state={{ supersededLink: phase.supersededLink }}
        replace
      />
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/* The brand header is deliberately NOT rendered while resolving: a permitted staffer would see
          a customer-branded flash on every NetSuite click, which is the highest-frequency journey in
          this design. It mounts only once the minimal branch is actually chosen. */}
      {phase.state === 'minimal' && (
        <header className="flex items-center gap-3 border-b border-border px-4 py-3 sm:px-6">
          {/* The real brand mark — same asset the sidebar and login use, so a customer sees the
              identity they already recognise from our paperwork. */}
          <img src={logoOnly} alt="" className="h-9 w-auto shrink-0" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold leading-tight">Capitol Steel Corporation</p>
            {/* The family, not the word "Document" (86d3yek7a). The server has always returned this
                label and nothing rendered it. It matters more now that all six families reach this
                page: a customer who followed a link should see "Credit Memo" or "Delivery Receipt",
                not a generic word that tells them nothing about which paper they were sent. Falls
                back rather than rendering an empty line if the label is ever missing. */}
            <p className="truncate text-xs text-muted-foreground">{phase.doc.familyLabel || 'Document'}</p>
          </div>
        </header>
      )}

      {/* Full width on purpose: this page is a document reader, and an A4 scan at a readable zoom
          needs every pixel. The narrow reading column that suits prose actively hurts here. */}
      <main className="flex w-full flex-1 flex-col px-4 py-6 sm:px-6">
        {phase.state === 'resolving' && (
          <div className="flex min-h-0 flex-1 flex-col gap-4" aria-busy="true">
            <Skeleton className="h-6 w-48 shrink-0" />
            <Skeleton className="min-h-0 w-full flex-1 rounded-lg" />
            <span className="sr-only">Opening document…</span>
          </div>
        )}

        {phase.state === 'error' && (
          <EmptyState
            icon={RotateCcw}
            title="Couldn’t open this document"
            description="Something went wrong reaching the server. This isn’t about the document itself — try again in a moment."
            action={
              <Button variant="outline" size="sm" onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </Button>
            }
          />
        )}

        {phase.state === 'unavailable' && (
          <div className="space-y-6">
            <EmptyState
              icon={FileQuestion}
              title="This document isn’t available"
              description="The link may have expired or been withdrawn. If you need a copy, please ask your Capitol Steel contact for a fresh link."
            />
            <StaffHint isAuthenticated={isAuthenticated} userName={userName} token={token} />
          </div>
        )}

        {phase.state === 'minimal' && (
          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="font-mono text-lg font-semibold">{phase.doc.docNumber}</h1>
              <Button onClick={download} className="gap-1.5">
                <Download className="h-4 w-4" aria-hidden="true" />
                Download PDF
              </Button>
            </div>

            {/* h-auto + flex-1 hands the viewer the rest of the viewport instead of its default
                fixed height — a scanned invoice is unreadable in a short frame. */}
            <PdfDocumentViewer
              url={pdfUrl}
              title={phase.doc.docNumber}
              className="h-auto min-h-0 flex-1"
            />

            <StaffHint isAuthenticated={isAuthenticated} userName={userName} token={token} />
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * The staff escape hatch. Only offered when NOT signed in: `PublicRoute` bounces an authenticated
 * visitor of /login straight to `/`, so showing it to a signed-in user is a guaranteed dead end.
 * A signed-in user who landed here instead lacks the family's permission — tell them that plainly.
 *
 * The return target goes through router state, never a `?returnUrl=` query param: a param handed to
 * `window.location` is an open-redirect surface, and router state is same-origin by construction.
 */
function StaffHint({
  isAuthenticated,
  userName,
  token,
}: {
  isAuthenticated: boolean;
  userName: string | null;
  token: string | undefined;
}) {
  if (isAuthenticated) {
    return (
      <p className="text-center text-xs text-muted-foreground">
        Signed in{userName ? ` as ${userName}` : ''} — the full document view requires permission for
        this document type.
      </p>
    );
  }

  return (
    <p className="text-center text-xs text-muted-foreground">
      Capitol Steel staff?{' '}
      <Link
        to="/login"
        state={{ from: token ? `/p/${token}` : '/' }}
        className="font-medium text-primary hover:underline"
      >
        Sign in
      </Link>{' '}
      to see the full document.
    </p>
  );
}
